/* eslint-disable no-param-reassign */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { objectOf, any, node, shape, func } from 'prop-types';
import { debounce } from 'underscore';
import { PreferenceService } from '@bingads-webui/preference-service';
import defaultCreater from './preference-default';

const scope = 'SmartPage';
const preferenceContext = createContext();
const handleRealKey = key => `${scope}.${key}`;
export const PreferenceContextProvider = ({
  i18n,
  serverContext,
  children,
}) => {
  const initState = useMemo(
    () =>
      Object.entries(defaultCreater(i18n, serverContext)).reduce(
        (acc, [name, preferenceDefaultValue]) => {
          const preferenceName = handleRealKey(name);
          const backEndValue = PreferenceService.findByNameAtUserLevel(preferenceName);
          acc[preferenceName] =
            backEndValue && backEndValue.Data !== undefined
              ? backEndValue.Data
              : preferenceDefaultValue;
          return acc;
        },
        {}
      ),
    [i18n, serverContext]
  );
  const value = useState(initState);
  return (
    <preferenceContext.Provider value={value}>
      {children}
    </preferenceContext.Provider>
  );
};

PreferenceContextProvider.propTypes = {
  serverContext: objectOf(any).isRequired,
  children: node.isRequired,
  i18n: shape({
    getString: func.isRequired,
  }).isRequired,
};

const savePreferenceAtBackEnd = debounce((key, value) => {
  PreferenceService.setAtUserLevel({
    Name: key,
    Data: value,
  });
}, 300);

export const usePreference = (name) => {
  const preferenceName = handleRealKey(name);
  const [allPreferenceState, setAllPreferenceState] = useContext(preferenceContext);
  if (!Object.keys(allPreferenceState).includes(preferenceName)) {
    throw new Error(`Smart page preference key not define: ${preferenceName}`);
  }
  const curPreference = allPreferenceState[preferenceName];
  const updatePreference = useCallback(
    (value) => {
      setAllPreferenceState(s => ({
        ...s,
        [preferenceName]: value,
      }));
      savePreferenceAtBackEnd(preferenceName, value);
    },
    [preferenceName, setAllPreferenceState]
  );
  return [curPreference, updatePreference];
};
